import { FC, ReactNode, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { comissaoService } from '@/services/comissaoService'
import { formatarCnpj } from '@/utils/cnpj'
import { NotaFiscalComissao, NotaFiscalExtracao, StatusExtracaoNotaFiscal } from '@/types/comissao'
import { brl } from './estadoFinanceiro'
import { SeloLeitura } from './ConferirNotaDialog'
import { ROTULO_REGIME, ROTULO_SITUACAO, rotuloPendencia } from './notaFiscalRotulos'

/** Abaixo disso o campo lido ganha um aviso de "confira" — a leitura veio de OCR + IA. */
const CONFIANCA_BAIXA = 0.7

export const ROTULO_LEITURA: Record<
  StatusExtracaoNotaFiscal,
  { rotulo: string; cor: 'default' | 'info' | 'success' | 'warning' | 'error' }
> = {
  PENDENTE: { rotulo: 'Lendo a nota…', cor: 'info' },
  CONCLUIDA: { rotulo: 'Dados lidos', cor: 'success' },
  SEM_DADOS: { rotulo: 'Não reconhecida', cor: 'warning' },
  FALHOU: { rotulo: 'Leitura falhou', cor: 'error' },
}

const data = (iso?: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY') : null)
const dinheiro = (v?: number | null) => (v == null ? null : brl(v))

interface Props {
  /** Nota da lista — mostrada na hora; o detalhe é recarregado do backend ao abrir. */
  nota: NotaFiscalComissao | null
  /** Back-office vê as divergências com o informado (decisão de 23/10: só alerta). */
  podeOperar: boolean
  onFechar: () => void
}

/**
 * "Dados da nota" — o que a leitura automática (OCR + IA) encontrou no arquivo da NF de
 * comissão, campo a campo, com aviso nos de baixa confiança. Para o back-office, também as
 * divergências entre o lido e o informado pelo consultor.
 */
const DadosNotaDialog: FC<Props> = ({ nota, podeOperar, onFechar }) => {
  const [atual, setAtual] = useState<NotaFiscalComissao | null>(nota)
  const [carregando, setCarregando] = useState(false)

  useEffect(() => {
    setAtual(nota)
    if (!nota) return
    let vivo = true
    setCarregando(true)
    comissaoService
      .notaFiscal(nota.id)
      .then((n) => vivo && setAtual(n))
      .catch(() => undefined) // fica com a versão da lista
      .finally(() => vivo && setCarregando(false))
    return () => {
      vivo = false
    }
  }, [nota])

  const e = atual?.extracao ?? null

  return (
    <Dialog open={nota !== null} onClose={onFechar} fullWidth maxWidth="md">
      <DialogTitle>
        Dados da nota — {atual?.numero}
        {e && (
          <Chip
            size="small"
            sx={{ ml: 1 }}
            label={ROTULO_LEITURA[e.status].rotulo}
            color={ROTULO_LEITURA[e.status].cor}
          />
        )}
        {e?.status === 'CONCLUIDA' && (
          <Box component="span" sx={{ ml: 1 }}>
            <SeloLeitura extracao={e} />
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {atual && <Andamento nota={atual} />}
          {carregando && !e ? (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress aria-label="Carregando dados da nota" />
            </Box>
          ) : !e ? (
            <Alert severity="info">Anexe o arquivo da nota para os dados serem lidos.</Alert>
          ) : (
            <Conteudo extracao={e} podeOperar={podeOperar} />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}

const Conteudo: FC<{ extracao: NotaFiscalExtracao; podeOperar: boolean }> = ({
  extracao: e,
  podeOperar,
}) => {
  if (e.status === 'PENDENTE') {
    return (
      <Alert severity="info">
        A nota está sendo lida (OCR + IA). Isso costuma levar menos de um minuto; os dados
        aparecem aqui quando a leitura terminar.
      </Alert>
    )
  }
  if (e.status !== 'CONCLUIDA') {
    return (
      <Alert severity={e.status === 'FALHOU' ? 'error' : 'warning'}>
        {e.mensagem ?? 'Não foi possível ler os dados da nota.'} Os dados informados no registro
        seguem valendo.
      </Alert>
    )
  }

  const conf = e.confiancas ?? {}
  const campo = (chave: string, rotulo: string, valor: ReactNode | null, xs = 6) => (
    <Grid item xs={12} sm={xs} key={chave}>
      <Typography variant="caption" color="textSecondary">
        {rotulo}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
        {valor ?? '—'}
        {valor != null && conf[chave] != null && conf[chave] < CONFIANCA_BAIXA && (
          <Chip size="small" label="confira" color="warning" variant="outlined" sx={{ ml: 1 }} />
        )}
      </Typography>
    </Grid>
  )
  const retido = e.issRetido == null ? null : e.issRetido ? 'Sim' : 'Não'

  return (
    <Stack spacing={2}>
      {podeOperar &&
        (e.divergencias.length > 0 ? (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              A leitura não bate com o informado pelo consultor (pré-alerta — só a conferência
              cria pendência):
            </Typography>
            {e.divergencias.map((d) => (
              <Typography variant="body2" key={d.campo}>
                {d.rotulo}: informado <strong>{d.informado}</strong>, lido{' '}
                <strong>{d.lido}</strong>
              </Typography>
            ))}
          </Alert>
        ) : (
          <Alert severity="success">Número, data e valor lidos batem com o informado.</Alert>
        ))}

      <Typography variant="caption" color="textSecondary">
        Leitura automática do arquivo (OCR + IA). Campos marcados com “confira” tiveram
        leitura de baixa confiança.
      </Typography>

      <Secao titulo="Identificação">
        {campo('numero', 'Número', e.numero ?? null, 3)}
        {campo('serie', 'Série', e.serie ?? null, 3)}
        {campo('dataEmissao', 'Emissão', data(e.dataEmissao), 3)}
        {campo('codigoVerificacao', 'Código de verificação', e.codigoVerificacao ?? null, 3)}
        {campo('municipio', 'Município', e.municipio ?? null, 12)}
      </Secao>

      <Secao titulo="Prestador e tomador">
        {campo('emitenteRazaoSocial', 'Prestador', e.emitenteRazaoSocial ?? null)}
        {campo('emitenteCnpj', 'CNPJ do prestador', e.emitenteCnpj ? formatarCnpj(e.emitenteCnpj) : null)}
        {campo('tomadorRazaoSocial', 'Tomador', e.tomadorRazaoSocial ?? null)}
        {campo('tomadorCnpj', 'CNPJ do tomador', e.tomadorCnpj ? formatarCnpj(e.tomadorCnpj) : null)}
        {campo('discriminacao', 'Discriminação do serviço', e.discriminacao ?? null, 12)}
      </Secao>

      {(e.competencia || e.situacao || e.codigoServico || e.naturezaOperacao || e.chaveAcesso ||
        e.optanteSimples != null || e.regimeEspecial || e.layout) && (
        <Secao titulo="Dados fiscais lidos">
          {campo('competencia', 'Competência', e.competencia ? dayjs(e.competencia).format('MM/YYYY') : null, 3)}
          {campo('situacao', 'Situação', e.situacao ?? null, 3)}
          {campo('codigoServico', 'Código de serviço', e.codigoServico ?? null, 3)}
          {campo('layout', 'Layout', e.layout ?? null, 3)}
          {campo('naturezaOperacao', 'Natureza da operação', e.naturezaOperacao ?? null)}
          {campo(
            'optanteSimples',
            'Optante pelo Simples',
            e.optanteSimples == null ? null : e.optanteSimples ? 'Sim' : 'Não',
            3
          )}
          {campo('regimeEspecial', 'Regime especial', e.regimeEspecial ?? null, 3)}
          {campo('chaveAcesso', 'Chave de acesso', e.chaveAcesso ?? null, 12)}
        </Secao>
      )}

      <Secao titulo="Valores e impostos">
        {campo('valorServicos', 'Valor dos serviços', dinheiro(e.valorServicos), 4)}
        {campo('valorDeducoes', 'Deduções', dinheiro(e.valorDeducoes), 4)}
        {campo('valorLiquido', 'Valor líquido', dinheiro(e.valorLiquido), 4)}
        {campo(
          'aliquotaIss',
          'Alíquota do ISS',
          e.aliquotaIss == null ? null : `${e.aliquotaIss.toLocaleString('pt-BR')}%`,
          4
        )}
        {campo('valorIss', 'ISS', dinheiro(e.valorIss), 4)}
        {campo('issRetido', 'ISS retido', retido, 4)}
        {campo('valorIr', 'IR', dinheiro(e.valorIr), 4)}
        {campo('valorPis', 'PIS', dinheiro(e.valorPis), 4)}
        {campo('valorCofins', 'COFINS', dinheiro(e.valorCofins), 4)}
        {campo('valorCsll', 'CSLL', dinheiro(e.valorCsll), 4)}
        {campo('valorInss', 'INSS', dinheiro(e.valorInss), 4)}
      </Secao>
    </Stack>
  )
}

/**
 * AN-9 (Q36): pendências, conciliação do registro, conferência humana e aprovação fiscal —
 * tudo como o backend devolveu (snapshots); nada é recalculado aqui. Nunca mostra quem
 * conferiu ou aprovou (o consultor vê o que falta, não o nome do operador).
 */
const Andamento: FC<{ nota: NotaFiscalComissao }> = ({ nota }) => {
  const pendencias = nota.pendencias ?? []
  const abertas = pendencias.filter((p) => p.status === 'ABERTA')
  const c = nota.conferencia
  const f = nota.fiscal
  const temConciliacao = nota.somaComissoes != null
  if (!pendencias.length && !c && !f && !temConciliacao) return null
  const item = (rotulo: string, valor: ReactNode | null, xs = 4) => (
    <Grid item xs={12} sm={xs} key={rotulo}>
      <Typography variant="caption" color="textSecondary">
        {rotulo}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {valor ?? '—'}
      </Typography>
    </Grid>
  )
  return (
    <Stack spacing={2}>
      {abertas.length > 0 ? (
        <Alert severity="warning" data-testid="pendencias-nf">
          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Pendências que bloqueiam o pagamento:
          </Typography>
          {abertas.map((p) => (
            <Typography variant="body2" key={p.id}>
              <strong>{rotuloPendencia(p)}</strong>
              {' — '}
              {p.mensagem}
              {(p.esperado || p.encontrado) && (
                <>
                  {' '}(esperado {p.esperado ?? '—'}, encontrado {p.encontrado ?? '—'})
                </>
              )}
              {' · '}
              {p.responsavel === 'CONSULTOR' ? 'cabe ao consultor' : 'cabe ao Financeiro'}
            </Typography>
          ))}
        </Alert>
      ) : (
        c && <Alert severity="success">Conferência sem pendências abertas.</Alert>
      )}

      {temConciliacao && (
        <Secao titulo="Conciliação no registro">
          {item('Soma das comissões', brl(nota.somaComissoes))}
          {item('Diferença', brl(nota.diferencaConciliacao))}
          {item('Tolerância aplicada', brl(nota.toleranciaAplicada))}
        </Secao>
      )}

      {c && (
        <Secao titulo={`Conferência do Financeiro (versão ${c.versao})`}>
          {item('Número', c.numero, 3)}
          {item('Emissão', data(c.dataEmissao), 3)}
          {item('Competência', dayjs(c.competencia).format('MM/YYYY'), 3)}
          {item('Valor dos serviços', brl(c.valorServicos), 3)}
          {item('CNPJ do prestador', c.emitenteCnpj ? formatarCnpj(c.emitenteCnpj) : null, 6)}
          {item('CNPJ do tomador', c.tomadorCnpj ? formatarCnpj(c.tomadorCnpj) : null, 6)}
          {item(
            'Situação fiscal',
            `${ROTULO_SITUACAO[c.situacaoFiscal]} (${c.situacaoFonte === 'XML' ? 'pelo XML' : 'conferência manual'})`,
            6
          )}
          {item('Conferida em', dayjs(c.conferidoEm).format('DD/MM/YYYY HH:mm'), 6)}
        </Secao>
      )}

      {f && (
        <Secao titulo="Aprovação fiscal">
          {item('Regime', f.regimeTributario ? ROTULO_REGIME[f.regimeTributario] : null)}
          {item('Código de serviço', f.codigoServico ?? null)}
          {item('ISS retido', f.issRetido == null ? null : f.issRetido ? 'Sim' : 'Não')}
          {item('Natureza da operação', f.naturezaOperacao ?? null, 12)}
          {item('ISS', dinheiro(f.valorIssRetido))}
          {item('IR', dinheiro(f.valorIrRetido))}
          {item('INSS', dinheiro(f.valorInssRetido))}
          {item('PIS', dinheiro(f.valorPisRetido))}
          {item('COFINS', dinheiro(f.valorCofinsRetido))}
          {item('CSLL', dinheiro(f.valorCsllRetido))}
          {item('Líquido a pagar', dinheiro(f.valorLiquidoPagamento), 6)}
          {item('Observação', f.observacao ?? null, 6)}
        </Secao>
      )}
    </Stack>
  )
}

const Secao: FC<{ titulo: string; children: ReactNode }> = ({ titulo, children }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
      {titulo}
    </Typography>
    <Divider sx={{ mb: 1 }} />
    <Grid container spacing={1.5}>
      {children}
    </Grid>
  </Box>
)

export default DadosNotaDialog
